import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react'
import type { NavigateFunction } from 'react-router-dom'
import type {
  UiTestProgress,
  UiTestScenarioId,
  UiTestStepLog,
} from '@/modules/ui-test/uiTest.types'

export type UiTestRunContextValue = {
  active: boolean
  scenarioId: UiTestScenarioId | null
  progress: UiTestProgress | null
  logs: UiTestStepLog[]
  /** Vùng nội dung màn hình thật (layout chính). */
  screenRootRef: RefObject<HTMLDivElement | null>
  /** Tăng sau mỗi lần dữ liệu nghiệp vụ thay đổi — màn hình có thể reload. */
  dataRevision: number
  navigate: NavigateFunction
  setProgress: (progress: UiTestProgress | null) => void
  appendLog: (log: Omit<UiTestStepLog, 'at'>) => void
  getLogs: () => UiTestStepLog[]
  notifyDataChanged: () => void
}

const UiTestRunContext = createContext<UiTestRunContextValue | null>(null)

export function UiTestRunProvider({
  scenarioId,
  screenRootRef,
  navigate,
  children,
}: {
  scenarioId: UiTestScenarioId
  screenRootRef: RefObject<HTMLDivElement | null>
  navigate: NavigateFunction
  children: ReactNode
}) {
  const [progress, setProgress] = useState<UiTestProgress | null>(null)
  const [logs, setLogs] = useState<UiTestStepLog[]>([])
  const logsRef = useRef<UiTestStepLog[]>([])
  const [dataRevision, setDataRevision] = useState(0)

  const appendLog = useCallback((log: Omit<UiTestStepLog, 'at'>) => {
    const entry: UiTestStepLog = { ...log, at: new Date().toISOString() }
    logsRef.current = [entry, ...logsRef.current]
    setLogs(logsRef.current)
  }, [])

  const getLogs = useCallback(() => logsRef.current, [])

  const notifyDataChanged = useCallback(() => {
    setDataRevision((n) => n + 1)
  }, [])

  const value = useMemo<UiTestRunContextValue>(
    () => ({
      active: true,
      scenarioId,
      progress,
      logs,
      screenRootRef,
      dataRevision,
      navigate,
      setProgress,
      appendLog,
      getLogs,
      notifyDataChanged,
    }),
    [appendLog, dataRevision, getLogs, logs, navigate, notifyDataChanged, progress, scenarioId, screenRootRef],
  )

  return <UiTestRunContext.Provider value={value}>{children}</UiTestRunContext.Provider>
}

export function useUiTestRun(): UiTestRunContextValue {
  const ctx = useContext(UiTestRunContext)
  if (!ctx) {
    throw new Error('useUiTestRun must be used within UiTestRunProvider')
  }
  return ctx
}

export function useUiTestRunOptional(): UiTestRunContextValue | null {
  return useContext(UiTestRunContext)
}
